import { useState, useRef, useEffect } from "react";
import { X, TriangleAlert, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { FileIcon } from "../FileIcon";
import { springs } from "@/components/ui/motion";
import { UploadTagInput } from "./UploadTagInput";
import type { UploadFile } from "@/hooks/use-upload-documents";

/* ── file-type accent colors (hex for inline styles) ── */
const fileAccentHex: Record<string, string> = {
  pdf: "#ef4444",
  docx: "#3b82f6",
  xlsx: "#10b981",
  pptx: "#f97316",
  txt: "#6b7280",
  image: "#a855f7",
};

function getAccentHex(type: string) {
  return fileAccentHex[type] ?? "#6b7280";
}

/* ── extension label ── */
function getExtLabel(type: string) {
  const map: Record<string, string> = {
    pdf: "PDF",
    docx: "DOCX",
    xlsx: "XLSX",
    pptx: "PPTX",
    txt: "TXT",
    image: "IMG",
  };
  return map[type] ?? type.toUpperCase();
}

/* ── tailwind border-l color class by file type ── */
const accentBorderClass: Record<string, string> = {
  pdf: "border-l-red-500",
  docx: "border-l-blue-500",
  xlsx: "border-l-emerald-500",
  pptx: "border-l-orange-500",
  txt: "border-l-gray-500",
  image: "border-l-purple-500",
};

/* ── tailwind text color class for extension badge ── */
const accentTextClass: Record<string, string> = {
  pdf: "text-red-500 bg-red-500/10",
  docx: "text-blue-500 bg-blue-500/10",
  xlsx: "text-emerald-500 bg-emerald-500/10",
  pptx: "text-orange-500 bg-orange-500/10",
  txt: "text-gray-500 bg-gray-500/10",
  image: "text-purple-500 bg-purple-500/10",
};

interface UploadFileRowProps {
  file: UploadFile;
  onRename: (id: string, name: string) => void;
  onRemove: (id: string) => void;
  onAddTag: (fileId: string, tagId: string) => void;
  onRemoveTag: (fileId: string, tagId: string) => void;
  disabled?: boolean;
  isDuplicate?: boolean;
  validationError?: string;
}

export function UploadFileRow({
  file,
  onRename,
  onRemove,
  onAddTag,
  onRemoveTag,
  disabled,
  isDuplicate,
  validationError,
}: UploadFileRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(file.displayName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== file.displayName) {
      onRename(file.id, trimmed);
    } else {
      setEditValue(file.displayName);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      setEditValue(file.displayName);
      setIsEditing(false);
    }
  };

  const isUploading = disabled;
  const isComplete = isUploading && file.progress >= 100;
  const showProgress = isUploading && file.progress > 0 && !isComplete;
  const accentHex = getAccentHex(file.fileType);

  const borderClass = validationError
    ? "border-l-red-500"
    : isDuplicate
      ? "border-l-amber-500"
      : (accentBorderClass[file.fileType] ?? "border-l-gray-500");

  const extBadgeClass =
    accentTextClass[file.fileType] ?? "text-gray-500 bg-gray-500/10";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -6, scale: 0.97 }}
      transition={springs.smooth}
      className={`relative overflow-hidden rounded-lg border-l-[3px] ${borderClass} ${
        validationError
          ? "bg-red-500/[0.04] ring-1 ring-red-500/20"
          : isDuplicate
            ? "bg-amber-500/[0.04] ring-1 ring-amber-500/15"
            : "bg-card"
      } group`}
    >
      {/* ── progress background sweep ── */}
      {showProgress && (
        <div
          className="absolute inset-0 origin-left transition-transform duration-300 ease-out"
          style={{
            backgroundColor: `${accentHex}08`,
            transform: `scaleX(${file.progress / 100})`,
          }}
        />
      )}

      {/* ── completion background ── */}
      {isComplete && (
        <motion.div
          className="absolute inset-0 bg-emerald-500/[0.06]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        />
      )}

      {/* ── card content ── */}
      <div className="relative flex items-start gap-3 px-3 py-2.5">
        <FileIcon type={file.fileType} />

        <div className="flex-1 min-w-0">
          {/* Line 1: filename + extension badge */}
          <div className="flex items-center gap-2">
            {isEditing && !isUploading ? (
              <input
                ref={inputRef}
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                className="flex-1 min-w-0 bg-transparent text-sm text-primary outline-none pb-0.5"
                style={{
                  borderBottom: `1.5px solid ${accentHex}60`,
                }}
              />
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (!isUploading) {
                    setEditValue(file.displayName);
                    setIsEditing(true);
                  }
                }}
                className="flex-1 min-w-0 text-sm text-primary truncate text-left cursor-text hover:border-b hover:border-muted/40 border-b border-transparent transition-colors"
              >
                {file.displayName}
              </button>
            )}
            <span
              className={`shrink-0 text-[10px] font-semibold tracking-wide rounded px-1.5 py-0.5 ${extBadgeClass}`}
            >
              {getExtLabel(file.fileType)}
            </span>
          </div>

          {/* Validation error */}
          <AnimatePresence>
            {validationError && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.15 }}
                className="text-[11px] text-red-500 mt-0.5 font-medium"
              >
                {validationError}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Line 2: tags + duplicate warning */}
          <div className="flex items-center gap-1.5 mt-1.5 min-h-[22px]">
            <div className="flex-1 min-w-0">
              <UploadTagInput
                selectedTagIds={file.tags}
                onAdd={(tagId) => onAddTag(file.id, tagId)}
                onRemove={(tagId) => onRemoveTag(file.id, tagId)}
                disabled={isUploading}
              />
            </div>

            {/* Duplicate warning */}
            <AnimatePresence>
              {isDuplicate && !isUploading && (
                <motion.div
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={springs.snappy}
                  className="inline-flex items-center gap-1 shrink-0"
                >
                  <TriangleAlert
                    size={11}
                    className="text-amber-500 shrink-0"
                  />
                  <span className="text-[11px] font-medium text-amber-500">
                    Duplicate
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* ── right side: actions / progress % / checkmark ── */}
        {!isUploading && (
          <button
            type="button"
            onClick={() => onRemove(file.id)}
            className="p-1 rounded hover:bg-card-hover text-muted hover:text-red-400 transition-colors cursor-pointer shrink-0 opacity-0 group-hover:opacity-100"
            title="Remove file"
          >
            <X size={14} />
          </button>
        )}

        {/* Progress percentage */}
        {showProgress && (
          <span
            className="shrink-0 text-xs font-medium text-muted tabular-nums self-center"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {Math.round(file.progress)}%
          </span>
        )}

        {/* Completion checkmark */}
        {isComplete && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={springs.bouncy}
            className="shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-emerald-500/15 self-center"
          >
            <Check size={13} className="text-emerald-500" strokeWidth={2.5} />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
